import { useState } from 'react';
import { S } from '../../domain/state.js';
import { cats, cat } from '../../domain/categories.js';
import { recEvery } from '../../domain/recurring.js';
import { MONTHS } from '../../lib/format.js';
import { Sheet, Seg, confirmDialog } from '../../components/ui.jsx';
import { openModal, closeModal } from '../../app/nav.js';
import { recWrite, deleteRec } from '../../store/actions.js';
import { toast } from '../../store/ui.js';

export const FREQS = [[1, 'כל חודש'], [2, 'כל חודשיים'], [3, 'כל 3 חודשים'], [6, 'כל חצי שנה'], [12, 'פעם בשנה']];

export function openRecModal(id) {
  openModal(() => <RecModal id={id} />);
}

function RecModal({ id }) {
  const r = id != null ? S.rec.find((x) => String(x.id) === String(id)) : null;
  const [f, setF] = useState(() => ({
    type: r ? r.type || 'expense' : 'expense',
    description: r ? r.description : '',
    amount: r ? Math.abs(Number(r.amount) || 0) : '',
    cat: r ? r.cat : 'other',
    day: r ? r.day_of_month || 1 : new Date().getDate(),
    freq: r ? recEvery(r) : 1,
    month: r && r.start_month ? r.start_month : new Date().getMonth() + 1,
    source: r ? r.source || 'cash' : 'cash',
  }));
  const set = (k) => (e) => setF({ ...f, [k]: e && e.target ? e.target.value : e });
  const catOpts = cats(f.type);
  // קטגוריה שנמחקה או לא מוכרת נשמרת כמו שהיא, ולא מוחלפת בשקט
  const extraCat = f.cat && !catOpts.some((c) => c.id === f.cat) ? f.cat : null;

  async function save() {
    const amt = parseFloat(f.amount), day = parseInt(f.day, 10), fq = parseInt(f.freq, 10) || 1;
    if (!f.description.trim()) return toast('חסר תיאור');
    if (!(amt > 0)) return toast('הסכום חייב להיות גדול מ-0');
    if (!(day >= 1 && day <= 31)) return toast('יום בחודש בין 1 ל-31');
    const body = { description: f.description.trim(), amount: amt, type: f.type, cat: f.cat || 'other', day_of_month: day, source: f.type === 'income' ? 'cash' : f.source };
    // שדות התדירות נשלחים רק כשצריך, כדי שבסיס בלי העמודות ימשיך לעבוד בחיובים חודשיים
    if (fq > 1 || (r && recEvery(r) > 1)) { body.every_months = fq; body.start_month = fq > 1 ? parseInt(f.month, 10) : null; }
    try {
      await recWrite(r ? r.id : null, body);
      closeModal();
      toast(r ? '✅ החיוב עודכן' : '✅ חיוב קבוע נוסף');
    } catch (e) {
      toast('⚠️ ' + e.message);
    }
  }
  async function del() {
    if (!(await confirmDialog({ title: 'מחיקת חיוב קבוע', body: '"' + r.description + '" יימחק מהתחזית. העסקאות עצמן לא נפגעות.', okLabel: 'מחק', danger: true }))) return;
    try { await deleteRec(r.id); closeModal(); toast('🗑️ נמחק'); } catch (e) { toast('⚠️ ' + e.message); }
  }

  return (
    <Sheet title={r ? '✎ עריכת חיוב קבוע' : '➕ חיוב קבוע חדש'}>
      <div className="mb12"><Seg kind="type" value={f.type} onChange={(v) => setF({ ...f, type: v, cat: cats(v).some((c) => c.id === f.cat) ? f.cat : cats(v)[0].id })} options={[['expense', 'הוצאה', 'exp'], ['income', 'הכנסה', 'inc']]} /></div>
      <div className="row mb12" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: 2 }}><label className="lbl">תיאור</label><input value={f.description} onChange={set('description')} placeholder="שכר דירה, ביטוח רכב..." /></div>
        <div style={{ flex: 1 }}><label className="lbl">סכום (₪)</label><input type="number" inputMode="decimal" min="0" value={f.amount} onChange={set('amount')} /></div>
      </div>
      <div className="mb12">
        <label className="lbl">קטגוריה</label>
        <select value={f.cat} onChange={set('cat')}>
          {catOpts.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          {extraCat && <option value={extraCat}>{cat(f.type, extraCat).icon} {extraCat}</option>}
        </select>
      </div>
      <div className="row mb12" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 90px' }}><label className="lbl">יום בחודש</label><input type="number" min="1" max="31" value={f.day} onChange={set('day')} /></div>
        <div style={{ flex: 1 }}><label className="lbl">תדירות</label>
          <select value={f.freq} onChange={set('freq')}>{FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
      </div>
      {Number(f.freq) > 1 && (
        <div className="mb12"><label className="lbl">{Number(f.freq) === 12 ? 'באיזה חודש יורד' : 'חודש שבו הוא יורד (אחד מהם)'}</label>
          <select value={f.month} onChange={set('month')}>{MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
        </div>
      )}
      {f.type !== 'income' && (
        <div className="mb8">
          <label className="lbl">מאיפה יורד</label>
          <Seg value={f.source} onChange={(v) => setF({ ...f, source: v })} options={[['cash', '🏦 עו"ש'], ['credit', '💳 אשראי']]} />
          <div className="note mt6">{f.source === 'credit' ? 'חיוב בכרטיס כבר נמצא בתוך חיוב הכרטיס, ולכן לא יופיע בתזרים כשורה נפרדת.' : 'יורד ישירות מהעו"ש ומופיע בתזרים ביום שלו.'}</div>
        </div>
      )}
      <div className="row mt12">
        <button className="ubtn ubtn-primary" style={{ flex: 2 }} onClick={save}>שמור</button>
        {r && <button className="ubtn ubtn-ghost danger-text" style={{ flex: 1 }} onClick={del}>מחק</button>}
      </div>
    </Sheet>
  );
}
